/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { IWssEvent } from "./wss-event.interface";
import { IWssSubscriptionContext, WssSubscriptionFilter, WssSubscriptionHandler } from "./wss-subscription.types";

/**
 * Factory functions for creating common subscription filters and handlers.
 * These can be composed to build complex subscription logic with minimal code.
 */

/**
 * Creates a filter that only passes events for the entity matching the current route param.
 *
 * @param routeParamName - Name of the route param containing the entity ID (e.g., "conversationId")
 * @param eventDataKey - Key in the event data containing the entity ID (defaults to "id")
 * @returns A filter function
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.MESSAGE_CREATED,
 *   filter: forCurrentEntity("conversationId", "conversationId"),
 *   handler: () => this.refresh()
 * }
 * ```
 */
export function forCurrentEntity<T extends Record<string, unknown>>(
  routeParamName: string,
  eventDataKey = "id"
): WssSubscriptionFilter<T> {
  return (event: IWssEvent<T>, context: IWssSubscriptionContext): boolean => {
    const routeId = context.routeParams[routeParamName];
    const eventId = event.data[eventDataKey];
    return routeId != null && routeId === eventId;
  };
}

/**
 * Creates a filter that only passes events for the current partner.
 *
 * @returns A filter function
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.REPORT_CREATED,
 *   filter: forCurrentPartner(),
 *   handler: () => this.refreshList()
 * }
 * ```
 */
export function forCurrentPartner<T = Record<string, unknown>>(): WssSubscriptionFilter<T> {
  return (event: IWssEvent<T>, context: IWssSubscriptionContext): boolean => {
    const partnerId = context.routeParams.partnerId;
    return partnerId != null && event.partnerId === partnerId;
  };
}

/**
 * Combines multiple filters with AND logic.
 * Event must pass all filters to be handled.
 *
 * @param filters - Array of filter functions
 * @returns A combined filter function
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.MESSAGE_CREATED,
 *   filter: combineFilters([
 *     forCurrentPartner(),
 *     forCurrentEntity("conversationId", "conversationId")
 *   ]),
 *   handler: () => this.refresh()
 * }
 * ```
 */
export function combineFilters<T = Record<string, unknown>>(
  filters: WssSubscriptionFilter<T>[]
): WssSubscriptionFilter<T> {
  return (event: IWssEvent<T>, context: IWssSubscriptionContext): boolean => {
    return filters.every((filter) => filter(event, context));
  };
}

/**
 * Combines multiple filters with OR logic.
 * Event passes if any filter passes.
 *
 * @param filters - Array of filter functions
 * @returns A combined filter function
 */
export function anyFilter<T = Record<string, unknown>>(filters: WssSubscriptionFilter<T>[]): WssSubscriptionFilter<T> {
  return (event: IWssEvent<T>, context: IWssSubscriptionContext): boolean => {
    return filters.some((filter) => filter(event, context));
  };
}

/**
 * Creates a handler that calls a Vuex action to refresh data.
 *
 * @param actionFn - Function that returns a promise (typically a store action call)
 * @returns A handler function
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.MESSAGE_CREATED,
 *   handler: refetchViaAction(() => ConversationModule.fetchConversationDetail({
 *     partnerId: this.partnerId,
 *     conversationId: this.conversationId
 *   }))
 * }
 * ```
 */
export function refetchViaAction<T = Record<string, unknown>>(
  actionFn: (event: IWssEvent<T>, context: IWssSubscriptionContext) => Promise<unknown>
): WssSubscriptionHandler<T> {
  return async (event: IWssEvent<T>, context: IWssSubscriptionContext): Promise<void> => {
    try {
      await actionFn(event, context);
    } catch (error) {
      // Silently fail - the component should handle errors through its normal error handling
      console.warn("[WssSubscription] Error in refetch action:", error);
    }
  };
}

/**
 * Creates a handler that calls a method on the component.
 *
 * @param methodName - Name of the method to call on the component
 * @returns A handler function
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.MESSAGE_CREATED,
 *   handler: callMethod("refreshConversation")
 * }
 * ```
 */
export function callMethod<T = Record<string, unknown>>(methodName: string): WssSubscriptionHandler<T> {
  return (event: IWssEvent<T>, context: IWssSubscriptionContext): void => {
    const method = (context.component as Record<string, unknown>)[methodName];
    if (typeof method === "function") {
      method.call(context.component, event);
    } else {
      console.warn(`[WssSubscription] Method "${methodName}" not found on component`);
    }
  };
}

/**
 * Creates a handler that emits an event on the component.
 *
 * @param eventName - Name of the event to emit
 * @returns A handler function
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.MESSAGE_CREATED,
 *   handler: emitEvent("message-received")
 * }
 * ```
 */
export function emitEvent<T = Record<string, unknown>>(eventName: string): WssSubscriptionHandler<T> {
  return (event: IWssEvent<T>, context: IWssSubscriptionContext): void => {
    context.component.$emit(eventName, event);
  };
}

/**
 * Combines multiple handlers to run sequentially.
 *
 * @param handlers - Array of handler functions
 * @returns A combined handler function
 */
export function combineHandlers<T = Record<string, unknown>>(
  handlers: WssSubscriptionHandler<T>[]
): WssSubscriptionHandler<T> {
  return async (event: IWssEvent<T>, context: IWssSubscriptionContext): Promise<void> => {
    for (const handler of handlers) {
      await handler(event, context);
    }
  };
}
